import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiCheckCircle, FiClock, FiTrash2,
  FiCalendar, FiMapPin, FiBookOpen, FiUsers, FiAlertCircle, FiUserX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { recitativoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Recitativos.css';

const TURMA_LABELS = {
  1: '1ª turma · 3-6 anos',
  2: '2ª turma · 7-11 anos',
  3: '3ª turma · 12+ anos',
};

const Recitativos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recitativos, setRecitativos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const carregar = async () => {
    try {
      setLoading(true);
      const isAdmin = user?.permissao === 'admin' || user?.permissao === 'editor';
      const resp = isAdmin
        ? await recitativoService.listarTodos()
        : await recitativoService.listarMeus();
      setRecitativos(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar recitativos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir este recitativo? Essa ação não pode ser desfeita.')) return;
    try {
      await recitativoService.excluir(id);
      toast.success('Recitativo excluído');
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir recitativo');
    }
  };

  const filtrados = useMemo(() => {
    return recitativos.filter((r) => {
      if (filtroTurma && String(r.turma) !== String(filtroTurma)) return false;
      if (filtroStatus === 'pendente' && r.nao_marcadas === 0) return false;
      if (filtroStatus === 'concluido' && r.nao_marcadas > 0) return false;
      if (busca) {
        const q = busca.toLowerCase();
        const haystack = `${r.localidade} ${r.livro} ${r.capitulo_inicial}:${r.verso_inicial}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [recitativos, busca, filtroTurma, filtroStatus]);

  const fmtData = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '—'; }
  };

  const corPercentual = (p) => {
    if (p == null) return 'pct-neutro';
    if (p >= 70) return 'pct-bom';
    if (p >= 40) return 'pct-medio';
    return 'pct-baixo';
  };

  return (
    <div className="rec-page">
      <header className="rec-header">
        <div className="rec-header-info">
          <h1><FiBookOpen /> Recitativos</h1>
          <p>Acompanhe os recitativos enviados e a frequência da reunião de jovens.</p>
        </div>
        <div className="rec-header-acoes">
          <button
            className="rec-btn-faltas"
            onClick={() => navigate('/recitativos/faltas')}
          >
            <FiUserX /> <span>Ver faltas</span>
          </button>
          <button
            className="rec-btn-novo"
            onClick={() => navigate('/recitativos/novo')}
          >
            <FiPlus /> <span>Novo Recitativo</span>
          </button>
        </div>
      </header>

      <section className="rec-toolbar">
        <div className="rec-search">
          <FiSearch />
          <input
            type="text"
            placeholder="Buscar por livro, localidade…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="rec-filtros">
          <select value={filtroTurma} onChange={(e) => setFiltroTurma(e.target.value)} aria-label="Filtrar por turma">
            <option value="">Todas as turmas</option>
            <option value="1">1ª turma (3-6)</option>
            <option value="2">2ª turma (7-11)</option>
            <option value="3">3ª turma (12+)</option>
          </select>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} aria-label="Filtrar por status">
            <option value="">Todos os status</option>
            <option value="pendente">Pendentes de presença</option>
            <option value="concluido">Presenças concluídas</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="rec-loading">Carregando recitativos…</div>
      ) : filtrados.length === 0 ? (
        <div className="rec-vazio">
          <FiAlertCircle size={36} />
          <h3>Nenhum recitativo encontrado</h3>
          <p>{recitativos.length === 0
            ? 'Crie o primeiro lote de recitativos clicando em “Novo Recitativo”.'
            : 'Ajuste os filtros para ver outros lotes.'}</p>
          {recitativos.length === 0 && (
            <button className="rec-btn-novo" onClick={() => navigate('/recitativos/novo')}>
              <FiPlus /> Novo Recitativo
            </button>
          )}
        </div>
      ) : (
        <ul className="rec-lista">
          {filtrados.map((r) => {
            const concluido = r.nao_marcadas === 0 && r.total > 0;
            return (
              <li key={r.id} className="rec-card">
                <Link to={`/recitativos/${r.id}`} className="rec-card-link">
                  <div className="rec-card-top">
                    <span className={`rec-badge turma-${r.turma}`}>{TURMA_LABELS[r.turma]}</span>
                    <span className={`rec-badge tipo-${r.tipo_pessoa}`}>
                      {r.tipo_pessoa === 'irmao' ? 'Irmãos' : 'Irmãs'}
                    </span>
                    {concluido ? (
                      <span className="rec-badge status-ok"><FiCheckCircle /> Concluído</span>
                    ) : (
                      <span className="rec-badge status-pendente"><FiClock /> Pendente</span>
                    )}
                  </div>

                  <h3 className="rec-card-titulo">
                    {r.livro} {r.capitulo_inicial}:{r.verso_inicial}
                    {r.total > 1 && <span className="rec-card-total"> · {r.total} recitativos</span>}
                  </h3>

                  <div className="rec-card-meta">
                    <span><FiCalendar /> {fmtData(r.data_referencia)}</span>
                    <span><FiMapPin /> {r.localidade}</span>
                    {r.auxiliar?.nome && <span><FiUsers /> {r.auxiliar.nome}</span>}
                  </div>

                  <div className="rec-card-stats">
                    <div className={`rec-pct ${corPercentual(r.percentual)}`}>
                      <strong>{r.percentual != null ? `${r.percentual}%` : '—'}</strong>
                      <small>frequência</small>
                    </div>
                    <div className="rec-stat-detail">
                      <span className="rec-stat-pres">{r.presentes} presentes</span>
                      <span className="rec-stat-ausen">{r.ausentes} faltaram</span>
                      {r.nao_marcadas > 0 && (
                        <span className="rec-stat-pend">{r.nao_marcadas} sem registro</span>
                      )}
                    </div>
                  </div>
                </Link>
                <div className="rec-card-acoes">
                  <button
                    className="rec-btn-icon rec-btn-danger"
                    onClick={() => handleExcluir(r.id)}
                    title="Excluir"
                    aria-label="Excluir recitativo"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Recitativos;
